import { Dayjs } from "dayjs";
import { CompleteBooking } from "../../../types/Booking";
import { Facility } from "../../../types/Structure";
import { Structure } from "../../../types/Structure";
import { UserDTO } from "../../../types/User";
import {
  BookingScope,
  BookingStatus,
  BookingType,
} from "../../../enums/BookingScope";
import { FacilityType } from "../../../enums/FacilityType";
import { MatchType } from "../../../types/Result";
import {
  BOOKING_CUSTOM_TYPE_COLOR,
  BOOKING_TYPE_COLORS,
  isValidHexColor,
} from "../../../utils/bookingColor";
import {
  getBookingTypeLabel,
  getSportName,
  getStatusLabel,
} from "../../../utils/stringUtil";

/**
 * Modello "piatto" del report giornaliero, condiviso da PDF, Excel e
 * anteprima HTML: tutta la logica di etichette/colori sta qui, i renderer
 * si limitano a impaginare.
 */

export type ExportBookingRow = {
  time: string;
  typeLabel: string;
  isCustomType: boolean;
  bookingType?: BookingType;
  /** Colore effettivo del blocco (hex #rrggbb). */
  color: string;
  bookedBy: string;
  bookedByHost: boolean;
  contact: string;
  team1: string[];
  team2: string[];
  /** "A · B vs C · D", oppure stringa vuota se non ci sono giocatori. */
  participantsLabel: string;
  coach: string;
  notes: string;
  status: BookingStatus;
  statusLabel: string;
  isCancelled: boolean;
  scopeLabel: string;
  matchTypeLabel: string;
};

export type ExportFacility = {
  id: string;
  name: string;
  typeLabel: string;
  isLesson: boolean;
  rows: ExportBookingRow[];
};

export type ExportTypeTotal = {
  label: string;
  color: string;
  count: number;
};

export type ExportWeather = {
  min: number;
  max: number;
  windKmh: number | string;
  description?: string;
};

export type BookingsExport = {
  structureName: string;
  structureAddress: string;
  structureContact: string;
  date: Dayjs;
  /** Es. "giovedì 12 ottobre 2026" */
  dateLong: string;
  /** Es. "12/10/2026" */
  dateShort: string;
  /** Es. "2026-10-12" */
  dateIso: string;
  sportName: string;
  facilities: ExportFacility[];
  totals: {
    bookings: number;
    facilities: number;
    facilitiesWithBookings: number;
    byType: ExportTypeTotal[];
  };
  generatedAt: string;
  /** Nome file senza estensione. */
  fileBaseName: string;
  weather?: ExportWeather;
};

const fullName = (user?: UserDTO | null): string =>
  user ? [user.name, user.familyName].filter(Boolean).join(" ").trim() : "";

const isLessonFacility = (facility: Facility) => /lezion?/i.test(facility.name);

export const effectiveBookingColor = (booking: CompleteBooking): string => {
  if (isValidHexColor(booking.color)) return booking.color;
  if (booking.customType) return BOOKING_CUSTOM_TYPE_COLOR;
  return BOOKING_TYPE_COLORS[booking.bookingType] || BOOKING_CUSTOM_TYPE_COLOR;
};

const facilityTypeLabel = (type?: FacilityType): string =>
  type === FacilityType.Indoor
    ? "Indoor"
    : type === FacilityType.Outdoor
    ? "Outdoor"
    : "";

const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const toRow = (booking: CompleteBooking): ExportBookingRow => {
  const match = booking.match;
  const team1 = [fullName(match?.player1Team1), fullName(match?.player2Team1)]
    .filter(Boolean);
  const team2 = [fullName(match?.player1Team2), fullName(match?.player2Team2)]
    .filter(Boolean);

  let participantsLabel = "";
  if (team1.length || team2.length) {
    participantsLabel = [team1.join(" · "), team2.join(" · ")]
      .filter(Boolean)
      .join("  vs  ");
  }

  const user = booking.user;
  const bookedByHost = !!booking.createdByHost;
  const bookedBy = bookedByHost ? "Struttura" : fullName(user) || "Utente";
  const contact = bookedByHost
    ? ""
    : [user?.phoneNumber, user?.email].filter(Boolean).join(" · ");

  return {
    time: booking.time || "-",
    typeLabel: getBookingTypeLabel(booking),
    isCustomType: !!booking.customType,
    bookingType: booking.bookingType,
    color: effectiveBookingColor(booking),
    bookedBy,
    bookedByHost,
    contact,
    team1,
    team2,
    participantsLabel,
    coach: fullName(booking.coach),
    notes: (booking.notes || "").trim(),
    status: booking.status,
    statusLabel: getStatusLabel(booking.status) || "",
    isCancelled: booking.status === BookingStatus.Cancelled,
    scopeLabel: booking.scope === BookingScope.Open ? "Aperta" : "Privata",
    matchTypeLabel:
      match?.matchType === MatchType.Single
        ? "Singolo"
        : match?.matchType === MatchType.Double
        ? "Doppio"
        : "",
  };
};

const byTime = (a: ExportBookingRow, b: ExportBookingRow) =>
  a.time.localeCompare(b.time);

export type BuildExportParams = {
  facilities: Facility[];
  date: Dayjs;
  structure?: Structure;
  weather?: ExportWeather;
};

export const buildBookingsExport = ({
  facilities,
  date,
  structure,
  weather,
}: BuildExportParams): BookingsExport => {
  const dateIso = date.format("YYYY-MM-DD");

  // Stesso ordine della pagina: i campi "lezione" in coda.
  const ordered = [
    ...facilities.filter((f) => !isLessonFacility(f)),
    ...facilities.filter(isLessonFacility),
  ];

  const exportFacilities: ExportFacility[] = ordered.map((facility) => ({
    id: facility.id || facility.name,
    name: facility.name || "Campo",
    typeLabel: facilityTypeLabel(facility.type),
    isLesson: isLessonFacility(facility),
    rows: (facility.bookings || [])
      .filter((b) => !b.date || b.date === dateIso)
      .map(toRow)
      .sort(byTime),
  }));

  const allRows = exportFacilities.flatMap((f) => f.rows);
  const typeTotals = new Map<string, ExportTypeTotal>();
  for (const row of allRows) {
    const key = row.typeLabel;
    const existing = typeTotals.get(key);
    if (existing) existing.count += 1;
    else typeTotals.set(key, { label: key, color: row.color, count: 1 });
  }

  const sportName = facilities[0]?.sport?.name
    ? getSportName(facilities[0].sport.name)
    : "";

  const structureAddress = structure
    ? [
        structure.address,
        [structure.cap, structure.city].filter(Boolean).join(" "),
        structure.province ? `(${structure.province})` : "",
      ]
        .filter(Boolean)
        .join(", ")
        .replace(", (", " (")
    : "";

  const structureContact = structure
    ? [structure.contactPhone, structure.contactEmail].filter(Boolean).join(" · ")
    : "";

  return {
    structureName: structure?.name || "GetMove",
    structureAddress,
    structureContact,
    date,
    dateLong: capitalize(date.locale("it").format("dddd D MMMM YYYY")),
    dateShort: date.format("DD/MM/YYYY"),
    dateIso,
    sportName,
    facilities: exportFacilities,
    totals: {
      bookings: allRows.length,
      facilities: exportFacilities.length,
      facilitiesWithBookings: exportFacilities.filter((f) => f.rows.length > 0)
        .length,
      byType: Array.from(typeTotals.values()).sort((a, b) => b.count - a.count),
    },
    generatedAt: capitalize(
      new Intl.DateTimeFormat("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date())
    ),
    fileBaseName: `Prenotazioni ${date.format("YYYY-MM-DD")}${
      sportName ? ` ${sportName}` : ""
    }`,
    weather,
  };
};
